import React, { useEffect, useRef } from 'react';
import { Input, Button, Space, message, Select, Spin } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import AntdCustomButton from './buttons/AntdCustomButton';
import { BodyText } from './ui/Typography';
import debounce from 'lodash-es/debounce';
import Api from '../services/Api';
import { useTranslation } from 'react-i18next';
import SessionForm from './forms/SessionForm';
import { validateTagOrKeyword } from '../helpers/ValidateTags';

interface CustomTagsFieldProps {
  form: any;
  formItemName: string;
  initialValues: string[];
  addButtonLabel: string;
  addFurtherButtonLabel: string;
  tagsTitle: string;
  maxCount: number;
  disabled: boolean;
  onChange: (values: string[]) => void;
  mode?: 'input' | 'select';
  placeholder?: string;
  duplicateMessage?: string;
  existingValues?: string[];
  onValidationStateChange?: (hasErrors: boolean) => void;
}

const CustomTagsField: React.FC<CustomTagsFieldProps> = ({
  form,
  formItemName,
  initialValues,
  addButtonLabel,
  addFurtherButtonLabel,
  tagsTitle,
  maxCount,
  disabled,
  onChange,
  mode = 'input',
  placeholder,
  duplicateMessage,
  existingValues = [],
  onValidationStateChange
}) => {
  const { t } = useTranslation();
  const [showTags, setShowTags] = React.useState(initialValues.length > 0);
  const [tags, setTags] = React.useState<string[]>([]);
  const [editingValues, setEditingValues] = React.useState<string[]>([]);
  const [inputErrors, setInputErrors] = React.useState<boolean[]>([]);

  // For select mode
  const [fetching, setFetching] = React.useState(false);
  const [options, setOptions] = React.useState<any[]>([]);
  const fetchRef = React.useRef(0);
  const [showingSelectForIndex, setShowingSelectForIndex] = React.useState<
    number | null
  >(null);

  const initRef = useRef(false);
  const prevEditingValuesRef = useRef<string[]>([]);
  const prevExistingValuesRef = useRef<string[]>([]);
  const userModifiedRef = useRef(false);

  React.useEffect(() => {
    if (!initRef.current) {
      setTags(initialValues);
      setEditingValues(initialValues);
      initRef.current = true;
      return;
    }
    const incomingChanged = JSON.stringify(initialValues) !== JSON.stringify(tags);
    if (incomingChanged && !userModifiedRef.current) {
      setTags(initialValues);
      setEditingValues(initialValues);
    }
  }, [initialValues]);

  const validateTag = (tagValue: string): boolean => {
    return validateTagOrKeyword(tagValue);
  };

  const isDuplicate = (val: string, idx?: number) =>
    tags
      .map((t, i) => ({ t: t ? t.trim().toLowerCase() : '', i }))
      .filter(({ i }) => i !== idx)
      .some(({ t }) => t === (val ? val.trim().toLowerCase() : '')) ||
    existingValues?.includes(val ? val.trim().toLowerCase() : '');

  const handleAdd = () => {
    if (tags.length >= maxCount) return;
    const newTags = [...tags, ''];
    const newEditingValues = [...editingValues, ''];
    const newErrors = [...inputErrors, false];
    setTags(newTags);
    setEditingValues(newEditingValues);
    setInputErrors(newErrors);
    userModifiedRef.current = true;

    if (mode === 'select') {
      setShowingSelectForIndex(newTags.length - 1);
    }
  };

  const handleRemove = (idx: number) => {
    const newTags = tags.filter((_, i) => i !== idx);
    const newEditingValues = editingValues.filter((_, i) => i !== idx);
    const newErrors = inputErrors.filter((_, i) => i !== idx);
    setTags(newTags);
    setEditingValues(newEditingValues);
    setInputErrors(newErrors);
    userModifiedRef.current = true;

    if (newTags.length === 0) {
      setShowTags(false);
      onChange([]);
    } else {
      const filteredTags = newTags.filter((tag) => tag.trim() !== '');
      onChange(filteredTags);
    }

    if (showingSelectForIndex === idx) {
      setShowingSelectForIndex(null);
    }
  };

  const handleInputChange = (val: string, idx: number) => {
    const newTags = [...tags];
    newTags[idx] = val;
    setTags(newTags);
    const filteredTags = newTags.filter((tag) => tag.trim() !== '');
    onChange(filteredTags);
    userModifiedRef.current = true;
  };

  const handleSelectChange = (value: any, idx: number) => {
    let obj = null;
    if (value && typeof value === 'object' && value.value) {
      obj = JSON.parse(value.value);
    } else if (typeof value === 'string') {
      obj = { displayName: value, name: value };
    }

    // duplicate check
    if (isDuplicate(obj.displayName || obj.name, idx)) {
      message.error(duplicateMessage || t('This value already exists'));
      setShowingSelectForIndex(null);
      return;
    }

    const newTags = [...tags];
    newTags[idx] = value.value;

    setTags(newTags);
    setEditingValues(newTags);
    setShowingSelectForIndex(null);
    userModifiedRef.current = true;

    const filteredTags = newTags.filter((tag) => tag && tag.trim() !== '');
    onChange(filteredTags);
  };

  const handleShowTags = () => {
    setShowTags(true);
    if (tags.length === 0) {
      handleAdd();
    }
  };

  const debounceFetcher = React.useMemo(() => {
    const loadOptions = async (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;

      if (value.length < 3) {
        return;
      }

      setOptions([]);
      setFetching(true);

      let searchResult = await Api.findCategoryName(value);

      if (fetchId !== fetchRef.current) {
        return;
      }

      let newOptions = searchResult.data.map((category) => {
        return {
          key: category.name,
          value: JSON.stringify(category),
          label: category.name
        };
      });

      setOptions(newOptions);
      setFetching(false);
    };

    return debounce(loadOptions, 800);
  }, []);

  const renderInputField = (val: string, idx: number) => {
    if (mode === 'select' && showingSelectForIndex === idx) {
      return (
        <div className="w-[220px] sm:w-[380px] md:w-[423px]">
          <Select
            style={{ width: '100%' }}
            placeholder={placeholder || t('Add custom activity')}
            showSearch
            labelInValue
            filterOption={false}
            onSearch={debounceFetcher}
            notFoundContent={fetching ? <Spin size="small" /> : null}
            options={options}
            onChange={(value) => handleSelectChange(value, idx)}
            onBlur={() => setShowingSelectForIndex(null)}
            autoFocus
            allowClear
            className="search-activity-input"
          />
        </div>
      );
    }

    return (
      <Input
        value={editingValues[idx] || ''}
        disabled={disabled}
        placeholder={
          placeholder ||
          (mode === 'select'
            ? t('Click to search activities')
            : t('Add custom areas'))
        }
        readOnly={mode === 'select'}
        onClick={() => {
          if (mode === 'select' && !tags[idx]) {
            setShowingSelectForIndex(idx);
          }
        }}
        onChange={(e) => {
          if (mode === 'input') {
            const newEditing = [...editingValues];
            newEditing[idx] = e.target.value;
            setEditingValues(newEditing);
            // Reset error on change
            const newErrors = [...inputErrors];
            newErrors[idx] = false;
            setInputErrors(newErrors);
            userModifiedRef.current = true;
          }
        }}
        onBlur={() => {
          if (mode === 'input') {
            const newVal = editingValues[idx]?.trim() || '';
            const newErrors = [...inputErrors];
            if (!newVal) {
              handleInputChange('', idx);
              newErrors[idx] = false;
              setInputErrors(newErrors);
              return;
            }
            if (isDuplicate(newVal, idx)) {
              message.error(duplicateMessage || t('This value already exists'));
              newErrors[idx] = true;
              setInputErrors(newErrors);
              return;
            }
            if (!validateTag(newVal)) {
              message.error(
                t(
                  'The value must be between 2 and 100 characters long and can only contain letters (A-Z), numbers (0-9), dashes ( - ), and slashes ( / ). Special symbols are not allowed.'
                )
              );
              newErrors[idx] = true;
              setInputErrors(newErrors);
              return;
            }
            newErrors[idx] = false;
            setInputErrors(newErrors);
            handleInputChange(newVal, idx);
            userModifiedRef.current = true;
          }
        }}
        onPressEnter={(e) => {
          if (mode === 'input') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          }
        }}
        className="w-full sm:w-[380px] md:w-[423px]"
        style={inputErrors[idx] ? { border: '1px solid #ff4d4f' } : {}}
      />
    );
  };

  useEffect(() => {
    const editingChanged = JSON.stringify(editingValues) !== JSON.stringify(prevEditingValuesRef.current);
    const existingChanged = JSON.stringify(existingValues) !== JSON.stringify(prevExistingValuesRef.current);
    
    if (editingChanged || existingChanged) {
      setInputErrors((prevErrors) =>
        prevErrors.map((err, idx) =>
          err && !isDuplicate(editingValues[idx], idx) ? false : err
        )
      );
      
      prevEditingValuesRef.current = [...editingValues];
      prevExistingValuesRef.current = [...existingValues];
    }
  }, [editingValues, existingValues]);

  useEffect(() => {
    if (initialValues.length > 0 || tags.length > 0) {
      setShowTags(true);
    }
  }, [initialValues.length, tags.length]);

    useEffect(() => {
      if (onValidationStateChange) {
        const hasErrors = inputErrors.some((err) => err === true);
        onValidationStateChange(hasErrors);
      }
    }, [inputErrors, onValidationStateChange]);

  return (
    <SessionForm
      form={form}
      formName={`${formItemName}SessionForm`}
      formProps={{
        initialValues: { [formItemName]: initialValues },
        onValuesChange: (changedValues, allValues) => {
          if (changedValues[formItemName]) {
            const filteredTags = changedValues[formItemName].filter(tag => tag.trim() !== '');
            onChange(filteredTags);
          }
          return true;
        }
      }}
    >
      {!showTags && (
        <div className="flex">
          <AntdCustomButton
            appearance="icon"
            disabled={disabled}
            onClick={handleShowTags}
            style={{
              backgroundColor:
                disabled || tags.length >= maxCount ? '#fafafa' : '#fff',
              color:
                disabled || tags.length >= maxCount
                  ? 'rgba(0, 0, 0, 0.25)'
                  : 'rgba(0, 0, 0, 0.88)',
              cursor:
                disabled || tags.length >= maxCount ? 'not-allowed' : 'pointer'
            }}>
            {addButtonLabel}
          </AntdCustomButton>
        </div>
      )}

      {showTags && (
        <Space direction="vertical" style={{ width: '100%', gap: '18px' }}>
          {tags.length > 0 && tagsTitle && (
            <BodyText style={{ textAlign: 'left', fontWeight: 600,  marginBottom: 0 }}>
              {tagsTitle}
            </BodyText>
          )}

          {tags.map((val, idx) => (
            <Space key={idx} style={{ width: '100%' }} align="center">
              {renderInputField(val, idx)}
              <Button
                icon={<DeleteOutlined />}
                onClick={() => handleRemove(idx)}
                type="text"
                danger
              />
            </Space>
          ))}

          <div className="flex sm:justify-center ">
            <AntdCustomButton
              appearance="icon"
              onClick={handleAdd}
              style={{
                backgroundColor:
                  disabled || tags.length >= maxCount ? '#fafafa' : '#fff',
                color:
                  disabled || tags.length >= maxCount
                    ? 'rgba(0, 0, 0, 0.25)'
                    : 'rgba(0, 0, 0, 0.88)',
                cursor:
                  disabled || tags.length >= maxCount
                    ? 'not-allowed'
                    : 'pointer'
              }}
              disabled={disabled || tags.length >= maxCount}>
              {addFurtherButtonLabel}
            </AntdCustomButton>
          </div>
        </Space>
      )}
    </SessionForm>
  );
};

export default CustomTagsField;
